import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import type { TaskWithRelations } from '@shared/schema';
import { CheckCircle, RotateCcw } from 'lucide-react';
import { useState } from 'react';

interface RecurringTaskCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskWithRelations;
  onSuccess: () => void;
}

export default function RecurringTaskCompletionModal({
  isOpen,
  onClose,
  task,
  onSuccess,
}: RecurringTaskCompletionModalProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleCompleteForNow = async () => {
    setIsLoading(true);
    try {
      await apiRequest('PATCH', `/api/tasks/${task.id}`, {
        status: 'complete',
        completionType: 'for_now',
      });

      toast({
        title: 'Task Completed',
        description:
          'Task completed for this occurrence. It will recur as scheduled.',
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete task',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteFinally = async () => {
    setIsLoading(true);
    try {
      await apiRequest('PATCH', `/api/tasks/${task.id}`, {
        status: 'complete',
        completionType: 'finally',
        isRecurring: false, // Stop recurrence
      });

      toast({
        title: 'Task Completed Finally',
        description: 'Task completed and will no longer recur.',
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete task',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-blue-600" />
            Complete Recurring Task
          </DialogTitle>
          <DialogDescription>
            This is a recurring task. How would you like to complete it?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
              {task.title}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Recurrence: {task.recurrenceFrequency} • Next run:{' '}
              {task.recurrenceNextRun
                ? new Date(task.recurrenceNextRun).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>

          <div className="space-y-3">
            <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <h4 className="font-medium mb-1">Complete for Now</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Mark this occurrence as complete. The task will continue to
                recur according to its schedule.
              </p>
            </div>

            <div className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <h4 className="font-medium mb-1">Complete Finally</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Mark this occurrence as complete and stop the task from
                recurring further.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCompleteForNow}
            disabled={isLoading}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Complete for Now
          </Button>
          <Button
            onClick={handleCompleteFinally}
            disabled={isLoading}
            variant="destructive"
            className="w-full sm:w-auto"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Complete Finally
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
